import { canonicalizeMusic3Tag, normalizeMusic3Lyrics } from "./tags";
import { uid } from "./dials";

export type KaraokeRow =
  | { kind: "section"; id: string; label: string; cue?: number }
  | { kind: "rest"; id: string; label: string; cue?: number }
  | { kind: "line"; id: string; text: string; cue?: number };

export type TimedRow = KaraokeRow & { start: number; end: number };

export type KaraokeSinger = {
  id: string;
  name: string;
  points: number;
};

export type KaraokeEnvelope = {
  hop: number;
  rms: Float32Array;
};

const SINGERS_KEY = "musicforge.karaoke.singers.v1";
const LRC = /^\[(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?\]\s*(.*)$/;

export const KARAOKE_CAPTION =
  "KARAOKE BACKING TRACK. Music only. No lead vocal, no hummed melody standing in for a singer, no choir singing the words. Leave a hole for a live singer. [intro], [instrumental], and [outro] are music-only.";

function lrcSeconds(m: string, s: string, frac: string | undefined): number {
  const min = Number(m) || 0;
  const sec = Number(s) || 0;
  let extra = 0;
  if (frac) extra = frac.length <= 2 ? Number(frac) / 100 : Number(frac) / 1000;
  return min * 60 + sec + (Number.isFinite(extra) ? extra : 0);
}

export function karaokeRows(raw: string): KaraokeRow[] {
  // Do not run normalizeMusic3Lyrics first — it treats [mm:ss.xx] as a tag and strips cues.
  const rows: KaraokeRow[] = [];
  let n = 0;
  for (const line of String(raw || "").split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const lrc = trimmed.match(LRC);
    const text = lrc ? lrc[4].trim() : trimmed;
    const cue = lrc ? lrcSeconds(lrc[1], lrc[2], lrc[3]) : undefined;
    if (!text && cue == null) continue;
    const m = text.match(/^\[([^\]]+)\]$/);
    if (m) {
      const tag = canonicalizeMusic3Tag(m[1]) || m[1].toLowerCase().replace(/\s+/g, "-");
      if (tag === "instrumental" || tag === "solo" || tag === "intro" || tag === "outro" || tag === "inst") {
        const emptyIntro = tag === "intro" || tag === "outro";
        rows.push({
          kind: tag === "instrumental" || tag === "solo" || tag === "inst" || emptyIntro ? "rest" : "section",
          id: `r${n++}`,
          label: tag === "solo" ? "Solo" : tag === "intro" ? "Intro" : tag === "outro" ? "Outro" : "Instrumental",
          cue,
        });
      } else {
        rows.push({ kind: "section", id: `s${n++}`, label: tag, cue });
      }
      continue;
    }
    if (!text) continue;
    rows.push({ kind: "line", id: `l${n++}`, text, cue });
  }
  return foldEmptyIntroOutro(rows);
}

function foldEmptyIntroOutro(rows: KaraokeRow[]): KaraokeRow[] {
  return rows.map((row, i) => {
    if (row.kind !== "rest") return row;
    if (row.label !== "Intro" && row.label !== "Outro") return row;
    const next = rows[i + 1];
    if (next && next.kind === "line") {
      return { kind: "section", id: row.id, label: row.label.toLowerCase(), cue: row.cue };
    }
    return row;
  });
}

export function karaokeFormLyrics(raw: string): string {
  const { lyrics } = normalizeMusic3Lyrics(raw || "");
  if (/\[(instrumental|solo)\]/i.test(lyrics)) return lyrics;
  const lines = lyrics.split("\n");
  let chorus = -1;
  let after = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (/^\s*\[chorus\]\s*$/i.test(lines[i])) {
      chorus = i;
      after = i + 1;
      while (after < lines.length && !/^\s*\[[^\]]+\]\s*$/.test(lines[after])) after += 1;
      break;
    }
  }
  if (chorus >= 0) {
    lines.splice(after, 0, "[instrumental]");
    return lines.join("\n");
  }
  const outro = lines.findIndex((l) => /^\s*\[outro\]\s*$/i.test(l));
  if (outro >= 0) {
    lines.splice(outro, 0, "[instrumental]");
    return lines.join("\n");
  }
  lines.push("[instrumental]");
  return lines.join("\n");
}

export function rowWeight(row: KaraokeRow): number {
  if (row.kind === "section") return 0.35;
  if (row.kind === "rest") {
    const lab = row.label.toLowerCase();
    if (lab === "intro" || lab === "outro") return 2.4;
    if (lab === "solo") return 2.6;
    return 3.0;
  }
  // Word count with a floor so short chorus hooks keep bar time.
  const words = row.text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1.35, words * 0.62);
}

function blockWeight(block: KaraokeRow[]): number {
  const head = block[0];
  const lines = block.filter((r) => r.kind === "line");
  // Prefer near-equal line pacing inside a section (karaoke sings by bar, not by letter).
  const lineW = lines.length
    ? lines.reduce((a, r) => a + rowWeight(r), 0) / Math.max(1, Math.sqrt(lines.length))
    : 0;
  if (head.kind === "rest") return Math.max(2.6, lineW + rowWeight(head));
  const tag = head.kind === "section" ? head.label : "";
  if (tag === "chorus" || tag === "pre-chorus" || tag === "post-chorus") {
    return Math.max(1.6, lineW * 1.18 + lines.length * 0.35);
  }
  if (tag === "bridge") return Math.max(1.3, lineW * 1.08 + lines.length * 0.25);
  return Math.max(1.2, lineW + lines.length * 0.3 + 0.35);
}

function blocksOf(rows: KaraokeRow[]): KaraokeRow[][] {
  const blocks: KaraokeRow[][] = [];
  let cur: KaraokeRow[] = [];
  for (const row of rows) {
    if ((row.kind === "section" || row.kind === "rest") && cur.length) {
      blocks.push(cur);
      cur = [row];
    } else cur.push(row);
  }
  if (cur.length) blocks.push(cur);
  return blocks;
}

function applyTimes(rows: KaraokeRow[], starts: number[], duration: number): TimedRow[] {
  return rows.map((row, i) => {
    const start = starts[i] ?? 0;
    const end = i + 1 < rows.length ? starts[i + 1] ?? duration : duration;
    return { ...row, start, end: Math.max(start, end) };
  });
}

function timesFromCues(rows: KaraokeRow[], duration: number): TimedRow[] | null {
  const cues = rows.map((r) => (typeof r.cue === "number" && Number.isFinite(r.cue) ? r.cue : null));
  if (cues.filter((c) => c != null).length < 2) return null;
  let last = 0;
  const starts = rows.map((row, i) => {
    const c = cues[i];
    if (c != null) {
      last = Math.max(0, c);
      return last;
    }
    return last;
  });
  const lastCue = starts[starts.length - 1] ?? 0;
  const tail = Math.max(duration, lastCue + 2);
  return applyTimes(rows, starts, tail);
}

function longestLowGap(env: KaraokeEnvelope, duration: number): { start: number; end: number } | null {
  const { hop, rms } = env;
  if (!rms.length || duration <= 1) return null;
  const sorted = Float32Array.from(rms).sort();
  const med = sorted[Math.floor(sorted.length * 0.45)] || 0.001;
  const cut = med * 0.62;
  let best: { start: number; end: number } | null = null;
  let i = 0;
  while (i < rms.length) {
    if (rms[i] > cut) {
      i += 1;
      continue;
    }
    const a = i;
    while (i < rms.length && rms[i] <= cut) i += 1;
    const start = a * hop;
    const end = Math.min(duration, i * hop);
    if (end - start < 2.2) continue;
    if (start < duration * 0.08 || end > duration * 0.94) continue;
    if (!best || end - start > best.end - best.start) best = { start, end };
  }
  return best;
}

export function ensureMusicOnlySection(
  rows: KaraokeRow[],
  duration = 0,
  env?: KaraokeEnvelope | null,
): KaraokeRow[] {
  if (rows.some((r) => r.kind === "rest")) return rows;
  const rest: KaraokeRow = { kind: "rest", id: uid("kr"), label: "Instrumental" };
  const gap = env && duration > 0 ? longestLowGap(env, duration) : null;
  if (gap) {
    const frac = gap.start / duration;
    const i = Math.max(1, Math.min(rows.length - 1, Math.round(frac * rows.length)));
    return [...rows.slice(0, i), rest, ...rows.slice(i)];
  }
  const chorusAt = rows.findIndex((r) => r.kind === "section" && r.label === "chorus");
  if (chorusAt >= 0) {
    let j = chorusAt + 1;
    while (j < rows.length && rows[j].kind === "line") j += 1;
    return [...rows.slice(0, j), rest, ...rows.slice(j)];
  }
  if (!rows.length) return [rest];
  const i = Math.max(1, Math.floor(rows.length * 0.55));
  return [...rows.slice(0, i), rest, ...rows.slice(i)];
}

function noveltyPeaks(env: KaraokeEnvelope, duration: number, count: number): number[] {
  const { hop, rms } = env;
  if (rms.length < 8 || count < 1) return [];
  const nov: number[] = [];
  let prev = rms[0];
  for (let i = 0; i < rms.length; i += 1) {
    const d = rms[i] - prev;
    nov.push(d > 0 ? d : 0);
    prev = rms[i];
  }
  const win = Math.max(2, Math.round(0.6 / hop));
  const scored: { t: number; v: number }[] = [];
  for (let i = win; i < nov.length - win; i += 1) {
    let local = true;
    for (let k = i - win; k <= i + win; k += 1) {
      if (k !== i && nov[k] >= nov[i]) {
        local = false;
        break;
      }
    }
    if (!local) continue;
    const t = i * hop;
    if (t < duration * 0.07 || t > duration * 0.93) continue;
    scored.push({ t, v: nov[i] });
  }
  scored.sort((a, b) => b.v - a.v);
  const picked: number[] = [];
  const minSep = Math.max(3, duration / (count + 2));
  for (const p of scored) {
    if (picked.length >= count) break;
    if (picked.every((t) => Math.abs(t - p.t) >= minSep)) picked.push(p.t);
  }
  return picked.sort((a, b) => a - b);
}

function snapBoundaries(ideal: number[], duration: number, env?: KaraokeEnvelope | null): number[] {
  if (!env || ideal.length < 3) return ideal;
  const peaks = noveltyPeaks(env, duration, ideal.length + 2);
  if (!peaks.length) return ideal;
  const out = ideal.slice();
  const window = Math.max(2.5, duration * 0.08);
  for (let i = 1; i < out.length - 1; i += 1) {
    let best = out[i];
    let bestD = window;
    for (const p of peaks) {
      const d = Math.abs(p - out[i]);
      if (d < bestD && p > out[i - 1] + 1.2 && p < (out[i + 1] ?? duration) - 1.2) {
        best = p;
        bestD = d;
      }
    }
    out[i] = best;
  }
  return out;
}

export function timeKaraokeRows(
  rows: KaraokeRow[],
  durationSec: number,
  env?: KaraokeEnvelope | null,
): TimedRow[] {
  const duration = Math.max(0, durationSec);
  const withRest = ensureMusicOnlySection(rows, duration, env);
  if (!withRest.length) return [];
  const fromLrc = timesFromCues(withRest, duration);
  if (fromLrc) return fromLrc;
  if (duration <= 0.4) {
    return withRest.map((row) => ({ ...row, start: 0, end: 0 }));
  }
  const blocks = blocksOf(withRest);
  const weights = blocks.map(blockWeight);
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  const ideal = [0];
  let acc = 0;
  for (let i = 0; i < weights.length - 1; i += 1) {
    acc += weights[i];
    ideal.push((acc / total) * duration);
  }
  ideal.push(duration);
  const bounds = snapBoundaries(ideal, duration, env);
  const timed: TimedRow[] = [];
  let idx = 0;
  for (let b = 0; b < blocks.length; b += 1) {
    const block = blocks[b];
    const a = bounds[b] ?? 0;
    const z = bounds[b + 1] ?? duration;
    const span = Math.max(0.05, z - a);
    const inner = block.map(rowWeight);
    const sum = inner.reduce((x, y) => x + y, 0) || 1;
    let t = a;
    for (let j = 0; j < block.length; j += 1) {
      const next = j === block.length - 1 ? z : t + (inner[j] / sum) * span;
      timed.push({ ...block[j], start: t, end: Math.max(t, next) });
      t = next;
      idx += 1;
    }
    void idx;
  }
  return timed;
}

export function activeRowIndex(rows: TimedRow[] | KaraokeRow[], timeSec: number, durationSec = 0): number {
  if (!rows.length) return 0;
  const timed = rows as TimedRow[];
  if (typeof timed[0]?.start === "number") {
    const t = Math.max(0, timeSec);
    for (let i = 0; i < timed.length; i += 1) {
      if (t < timed[i].end - 0.02) return i;
    }
    return timed.length - 1;
  }
  const dur = durationSec > 0.4 ? durationSec : 0;
  if (dur <= 0) {
    const line = rows.findIndex((r) => r.kind === "line");
    return line >= 0 ? line : 0;
  }
  const t = Math.max(0, Math.min(dur, timeSec));
  const weights = rows.map(rowWeight);
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) return 0;
  let walk = (t / dur) * total;
  for (let i = 0; i < rows.length; i += 1) {
    walk -= weights[i];
    if (walk <= 0) return i;
  }
  return rows.length - 1;
}

export function loadSingers(): KaraokeSinger[] {
  try {
    const raw = localStorage.getItem(SINGERS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: KaraokeSinger[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== "object") continue;
      const rec = item as Record<string, unknown>;
      const name = typeof rec.name === "string" ? rec.name.trim().slice(0, 24) : "";
      if (!name) continue;
      const id = typeof rec.id === "string" && rec.id ? rec.id : uid("ks");
      const points = typeof rec.points === "number" && Number.isFinite(rec.points) ? Math.max(0, Math.round(rec.points)) : 0;
      out.push({ id, name, points });
    }
    return out.slice(0, 12);
  } catch {
    return [];
  }
}

export function saveSingers(list: KaraokeSinger[]): void {
  try {
    localStorage.setItem(SINGERS_KEY, JSON.stringify(list.slice(0, 12)));
  } catch {
    /* */
  }
}

export function addSinger(list: KaraokeSinger[], name: string): KaraokeSinger[] {
  const label = name.trim().slice(0, 24);
  if (!label) return list;
  if (list.some((s) => s.name.toLowerCase() === label.toLowerCase())) return list;
  if (list.length >= 12) return list;
  const next = [...list, { id: uid("ks"), name: label, points: 0 }];
  saveSingers(next);
  return next;
}

export function dropSinger(list: KaraokeSinger[], id: string): KaraokeSinger[] {
  const next = list.filter((s) => s.id !== id);
  saveSingers(next);
  return next;
}

export function bumpSinger(list: KaraokeSinger[], id: string): KaraokeSinger[] {
  const next = list.map((s) => (s.id === id ? { ...s, points: s.points + 1 } : s));
  saveSingers(next);
  return next;
}

export function nextSingerId(list: KaraokeSinger[], currentId: string | null): string | null {
  if (!list.length) return null;
  if (!currentId) return list[0].id;
  const i = list.findIndex((s) => s.id === currentId);
  if (i < 0) return list[0].id;
  return list[(i + 1) % list.length].id;
}
